import { IconButton, NumberField, SelectField, TextField, Toggle } from '../ui/controls.js';
import { PARAM_TYPES, renameParam, uniqueName, type ControllerDoc, type Param, type ParamType } from './controller-doc.js';
import { PreviewParam } from './PreviewPane.js';
import { paramRange, type PreviewValue } from './preview-model.js';

/** Preview values shown under each parameter while the preview is open. */
export interface ParamPreview {
  values: Record<string, PreviewValue>;
  set: (name: string, value: PreviewValue) => void;
}

/** The controller's parameters: add, rename (references follow), retype, default value, delete. */
export function ParametersPanel({ doc, edit, preview }: { doc: ControllerDoc; edit: (fn: (d: ControllerDoc) => void) => void; preview?: ParamPreview }) {
  const add = (type: ParamType) =>
    edit((d) => {
      const name = uniqueName(type === 'trigger' ? 'Trigger' : type === 'bool' ? 'Bool' : type === 'int' ? 'Int' : 'Float', d.parameters.map((p) => p.name));
      const p: Param = { name, type };
      if (type === 'bool') p.default = false;
      else if (type !== 'trigger') p.default = 0;
      d.parameters.push(p);
    });

  return (
    <div className="anim-params">
      <div className="anim-section-head">
        <span>Parameters</span>
        <div className="anim-add-row">
          {PARAM_TYPES.map((t) => (
            <button key={t} type="button" className="anim-chip" title={`Add a ${t} parameter`} onClick={() => add(t)}>
              + {t}
            </button>
          ))}
        </div>
      </div>
      {doc.parameters.length === 0 && <p className="muted anim-hint">No parameters. Scripts set them with set_float / set_bool / set_trigger.</p>}
      {doc.parameters.map((p, i) => (
        <div key={i} className="anim-param">
          <TextField
            mono
            value={p.name}
            onChange={(name) => {
              const clean = name.trim();
              if (!clean || clean === p.name || doc.parameters.some((q) => q.name === clean)) return;
              edit((d) => renameParam(d, p.name, clean));
            }}
          />
          <SelectField
            value={p.type}
            options={PARAM_TYPES}
            onChange={(type) =>
              edit((d) => {
                const q = d.parameters[i]!;
                q.type = type as ParamType;
                if (q.type === 'bool') q.default = Boolean(q.default);
                else if (q.type === 'trigger') delete q.default;
                else q.default = Number(q.default ?? 0) || 0;
              })
            }
          />
          <div className="anim-param-default">
            {p.type === 'bool' && <Toggle checked={p.default === true} onChange={(v) => edit((d) => void (d.parameters[i]!.default = v))} />}
            {(p.type === 'float' || p.type === 'int') && (
              <NumberField
                value={typeof p.default === 'number' ? p.default : 0}
                integer={p.type === 'int'}
                step={p.type === 'int' ? 1 : 0.1}
                onChange={(v) => edit((d) => void (d.parameters[i]!.default = v))}
              />
            )}
            {p.type === 'trigger' && <span className="muted">trigger</span>}
          </div>
          <IconButton icon="trash" title="Delete parameter" onClick={() => edit((d) => void d.parameters.splice(i, 1))} />
          {preview && (
            <PreviewParam
              type={p.type}
              value={preview.values[p.name]}
              range={p.type === 'float' || p.type === 'int' ? paramRange(doc, p.name) : [0, 1]}
              onChange={(v) => preview.set(p.name, v)}
              isDefault={p.type === 'bool' ? (p.default === true) === preview.values[p.name] : (typeof p.default === 'number' ? p.default : 0) === preview.values[p.name]}
              onApply={() => edit((d) => void (d.parameters[i]!.default = preview.values[p.name]))}
            />
          )}
        </div>
      ))}
    </div>
  );
}
